/** @jsxImportSource @emotion/react */
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import { useTheme } from '@emotion/react';
import TextField from '@TextField';
import { includes, map } from 'lodash';
import { batch, useDispatch, useSelector } from 'react-redux';
import ValidationStatus from '../../Data/ValidationStatus';
import { getErrorMessage } from '../../Helpers/validation';
import { getAutoCompleteOptionsAction } from '../../Lib/redux/sagas/GetAutoCompleteOptions/getAutoCompleteOptions';
import {
  clearConstraintsValidations,
  setAutoCompleteInputValue,
  setComponentValidationStatus,
  updateValue,
} from '../../Lib/redux/slices/components';
import QuestionText from '../QuestionText/QuestionText';
import makeAutocompleteStyling from './makeAutocompleteStyling';
import { getComponentValidationStatus } from '../__helpers__/component.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const AutoCompleteInput = ({
  componentId,
  iFrameId,
  subheading,
  text,
  formId,
}) => {
  const textfieldExperimentName = useGetExperimentName({
    componentId,
    componentName: 'TextField',
  });
  const dispatch = useDispatch();
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const supplierMatches = useSelector((store) => store.supplierMatches);
  const showSmartMatchMeter = useSelector(
    (store) => store.features.showSmartMatchMeter
  );
  const showSmartMatchMeterV2 = useSelector(
    (store) => store.features.showSmartMatchMeterV2
  );
  const errorMessage = getErrorMessage(component.validation?.constraints);
  const { options, inputValue, placeholder, autoCompleteFreeTextEnabled } =
    component.input;
  const componentStatus = getComponentValidationStatus({ component });
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;
  const theme = useTheme();
  const styling = makeAutocompleteStyling(theme);
  const optionsArrayLowercase = map(options, (option) =>
    option.label.toLowerCase()
  );

  const handleInputChange = (newInputValue, event) => {
    if (event !== null && event.type !== 'click') {
      dispatch(
        getAutoCompleteOptionsAction({ componentId, value: newInputValue })
      );

      if (autoCompleteFreeTextEnabled) {
        batch(() => {
          dispatch(
            setAutoCompleteInputValue({ componentId, value: newInputValue })
          );
          dispatch(updateValue({ componentId, value: newInputValue }));
        });
      } else {
        batch(() => {
          dispatch(
            setAutoCompleteInputValue({ componentId, value: newInputValue })
          );
          dispatch(updateValue({ componentId, value: '' }));
        });
      }
    }
  };

  const handleChange = (value) => {
    batch(() => {
      dispatch(setAutoCompleteInputValue({ componentId, value }));
      dispatch(updateValue({ componentId, value }));
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const handleBlur = (event) => {
    // eslint-disable-next-line no-param-reassign
    event.defaultMuiPrevented = true;
    if (autoCompleteFreeTextEnabled) {
      batch(() => {
        dispatch(setAutoCompleteInputValue({ componentId, value: inputValue }));
        dispatch(updateValue({ componentId, value: inputValue }));
      });
    } else {
      dispatch(setAutoCompleteInputValue({ componentId, value: inputValue }));
      if (
        inputValue &&
        includes(optionsArrayLowercase, inputValue.toLowerCase())
      ) {
        dispatch(updateValue({ componentId, value: inputValue }));
      }
    }
    batch(() => {
      dispatch(setComponentValidationStatus({ componentId, iFrameId, formId }));
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const handleClick = () => {
    dispatch(
      clearConstraintsValidations({ componentId: component.component.id })
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      // eslint-disable-next-line no-param-reassign
      event.defaultMuiPrevented = true;
      event.target.blur();
    }
  };

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
          supplierMatches,
          showSmartMatchMeter,
          showSmartMatchMeterV2,
        })}
        subheading={renderMustache({
          template: subheading,
          components,
          currentComponent: component,
          iFrameId,
          formId,
          supplierMatches,
          showSmartMatchMeter,
          showSmartMatchMeterV2,
        })}
      />
      <Box
        marginTop={theme.mvf.spacing.medium}
        data-testid="autocompleteContainer"
      >
        <Autocomplete
          css={styling}
          freeSolo={autoCompleteFreeTextEnabled}
          disableClearable
          getOptionLabel={(option) => option.label || ''}
          id={`autocomplete-${componentId}`}
          inputValue={inputValue || ''}
          onInputChange={(event, newInputValue) => {
            handleInputChange(newInputValue, event);
          }}
          onChange={(_event, newValue) => {
            handleChange(newValue.label);
          }}
          onKeyDown={(event) => handleKeyDown(event)}
          options={options || []}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          onBlur={(e) => handleBlur(e)}
          renderInput={(params) => (
            <TextField
              inputProps={params.inputProps}
              inputRef={params.InputProps.ref}
              fullWidth
              type="text"
              placeholder={placeholder}
              dataTestId="autoCompleteTextField"
              errorMessage={errorMessage}
              onClick={() => handleClick()}
              isAnswered={isAnswered}
              experimentName={textfieldExperimentName}
            />
          )}
        />
      </Box>
    </>
  );
};

export default AutoCompleteInput;
